import React, { useState, useMemo } from "react";
import { Container, Row, Col, Card, Button, Offcanvas } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaBuilding, FaBook, FaUtensils, 
  FaMicroscope, FaVrCardboard, FaUniversity,
  FaChevronRight, FaPlay, FaMapMarkedAlt,
  FaList
} from "react-icons/fa";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";

const CampusTour = () => {
  // PRESERVED ALL LINKS CAREFULLY
  const campusData = useMemo(() => ({
    "Academic Block 1": {
      icon: <FaMicroscope />,
      desc: "Innovation Hub containing AI and Smart Labs.",
      items: [
        { name: "AI Lab", link: "https://kuula.co/share/collection/7Zfv9?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Smart Lab", link: "https://kuula.co/share/collection/7Zkxy?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "AB1 Inside", link: "https://kuula.co/share/collection/7Z0V0?logo=1&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "AB1 Outside", link: "https://kuula.co/share/collection/7ZmJl?logo=1&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Academic Block 2": {
      icon: <FaBuilding />,
      desc: "Primary lecture halls and faculty offices.",
      items: [
        { name: "AB2 Inside", link: "https://kuula.co/share/collection/7Zkxs?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "AB2 Outside", link: "https://kuula.co/share/collection/7Zkxj?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Academic Block 3": {
      icon: <FaBuilding />,
      desc: "Home to the Fab Lab and technical workshops.",
      items: [
        { name: "Fab Lab", link: "https://kuula.co/share/collection/7ZkxR?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Inside AB3", link: "https://kuula.co/share/collection/7Zkx2?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Outside AB3", link: "https://kuula.co/share/collection/7Zkx3?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Academic Block 5": {
      icon: <FaBuilding />,
      desc: "Advanced research labs and CRAIB.",
      items: [
        { name: "CRAIB Lab", link: "https://kuula.co/share/collection/7Znxy?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "AB5", link: "https://kuula.co/share/collection/7Zkxg?logo=1&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Library": {
      icon: <FaBook />,
      desc: "Multi-story Knowledge Center and Discussion Rooms.",
      items: [
        { name: "Inside Library", link: "https://kuula.co/share/collection/7ZmJh?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Outside Library", link: "https://kuula.co/share/collection/7Z0VX?logo=1&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Discussion Room", link: "https://kuula.co/share/collection/7Z0Vk?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Central Cafeteria": {
      icon: <FaUtensils />,
      desc: "The heart of student social life and dining.",
      items: [
        { name: "Main Cafeteria", link: "https://kuula.co/share/collection/7ZmJv?logo=1&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Auditorium": {
      icon: <FaVrCardboard />,
      desc: "Grand hall for events and international conferences.",
      items: [
        { name: "Grand Auditorium", link: "https://kuula.co/share/collection/7Z0Vg?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "University": {
      icon: <FaUniversity />,
      desc: "General campus view, grounds, and landmarks.",
      items: [
        { name: "Overall University", link: "https://kuula.co/share/collection/7ZhT9?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Zero Point", link: "https://kuula.co/share/collection/7Z0jZ?logo=1&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "Cricket Ground", link: "https://kuula.co/share/collection/7Z0tK?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
        { name: "AB3 Side Portion", link: "https://kuula.co/share/collection/7Z1XG?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
    "Administration Block": {
      icon: <FaBuilding />,
      desc: "Gateway to official university affairs.",
      items: [
        { name: "Administration", link: "https://kuula.co/share/collection/7ZhTZ?logo=0&info=1&fs=1&vr=0&sd=1&thumbs=1" },
      ]
    },
  }), []);

  const [activeKey, setActiveKey] = useState(Object.keys(campusData)[0]);
  const [showDrawer, setShowDrawer] = useState(false);

  return (
    <div className="campus-explorer-root">
      <Header />
      
      {/* Dynamic Hero Section */}
      <div className="explorer-hero position-relative overflow-hidden">
        <div className="hero-pattern-overlay"></div>
        <Container>
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="hero-content text-center position-relative" style={{ zIndex: 2 }}
          >
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="hero-icon-circle mb-4 shadow-lg"
            >
              <FaMapMarkedAlt />
            </motion.div>
            <h1 className="hero-title display-3 fw-900 mb-3">Campus <span className="text-warning">Virtual</span> Portal</h1>
            <p className="hero-subtitle mb-5 lead">Experience the world-class infrastructure of Sukkur IBA University in immersive 360° high-definition.</p>
            
            <Button 
                variant="primary" 
                className="rounded-pill px-5 py-3 d-lg-none nav-hint-pulse shadow-lg border-2 border-white fw-bold"
                onClick={() => setShowDrawer(true)}
            >
                <FaList className="me-2"/> Explore Locations
            </Button>
          </motion.div>
        </Container>
      </div>

      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
        <Row className="g-4">
            {/* Standardized Sticky Sidebar (Desktop) */}
            <Col lg={4} className="d-none d-lg-block">
                <div className="sticky-sidebar">
                    <div className="sidebar-card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="card-label">CAMPUS LOCATIONS</div>
                        <div className="program-list custom-scrollbar">
                            {Object.keys(campusData).map((key) => (
                                <button
                                key={key}
                                className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeKey === key ? 'is-active' : ''}`}
                                onClick={() => setActiveKey(key)}
                                >
                                <div className="d-flex align-items-center gap-3">
                                    <div className={`icon-circle-mini ${activeKey === key ? 'active' : ''}`}>
                                    {campusData[key].icon}
                                    </div>
                                    <div className="text-start">
                                        <div className="name fw-bold mb-0">{key}</div>
                                        <div className="stats-tag">{campusData[key].items.length} 360° Scenes</div>
                                    </div>
                                </div>
                                <FaChevronRight className={`chevron-mini ${activeKey === key ? 'visible' : ''}`} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </Col>

            {/* Content Detail View */}
            <Col lg={8} md={12}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeKey}
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -15 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white p-4 p-md-5 rounded-4 shadow-sm h-100"
                >
                  <div className="section-intro mb-5">
                    <div className="category-tag mb-2">Campus View</div>
                    <h2 className="portal-heading text-navy display-5 mb-3">{activeKey}</h2>
                    <div className="ivy-divider mb-4" />
                    <p className="editorial-text-body text-muted mb-0">{campusData[activeKey].desc}</p>
                  </div>

                  <Row className="g-4">
                    {campusData[activeKey].items.map((item, idx) => (
                      <Col xl={6} lg={12} md={6} key={idx}>
                        <motion.a 
                          href={item.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                          whileHover={{ y: -8 }} 
                          whileTap={{ scale: 0.98 }}
                        >
                          <Card className="tour-premium-card border-0 shadow-sm h-100 overflow-hidden">
                            <Card.Body className="p-0">
                                <div className="card-visual-header">
                                    <div className="visual-overlay"></div>
                                    <div className="play-hint">
                                        <FaPlay />
                                    </div>
                                </div>
                                <div className="p-4 d-flex align-items-center justify-content-between bg-white card-footer-area">
                                  <div className="tile-info">
                                    <h5 className="fw-900 text-dark mb-1">{item.name}</h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="live-dot-mini"></span>
                                        <small className="text-primary fw-bold text-uppercase ls-1" style={{fontSize: '0.65rem'}}>Interactive VR Scene</small>
                                    </div>
                                  </div>
                                  <div className="tour-action-circle shadow-sm">
                                    <FaChevronRight />
                                  </div>
                                </div>
                            </Card.Body>
                          </Card>
                        </motion.a>
                      </Col>
                    ))}
                  </Row>
                </motion.div>
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MOBILE LOCATION DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-50 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Location</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <div className="program-list-mobile">
            {Object.keys(campusData).map((key) => (
                <button 
                key={key} 
                className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeKey === key ? 'is-active' : ''}`}
                onClick={() => { setActiveKey(key); setShowDrawer(false); }}
                >
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle-mini ${activeKey === key ? 'active' : ''}`}>
                         {campusData[key].icon}
                    </div>
                    <div className="text-start">
                        <div className="name fw-bold mb-0 text-dark">{key}</div>
                        <div className="stats-tag">{campusData[key].items.length} VR Scenes</div>
                    </div>
                </div>
                {activeKey === key && <FaChevronRight className="chevron-mini visible" />}
                </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .campus-explorer-root { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
...
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync with Homepage */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1px; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        .editorial-text-body { font-size: 1.1rem; line-height: 1.6; }

        .explorer-hero {
          background: #001D3D;
          background: linear-gradient(135deg, #000814 0%, #001D3D 100%);
          padding: 60px 0 100px 0;
          color: white;
        }

        .hero-pattern-overlay {
            position: absolute; inset: 0;
            background-image: radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px);
            background-size: 30px 30px;
            opacity: 0.4;
        }

        .hero-icon-circle {
          width: 80px; height: 80px; background: rgba(255,255,255,0.08);
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          margin: 0 auto; font-size: 2.5rem; color: #FFC300;
          border: 1px solid rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
        }

        .hero-title { font-weight: 900; letter-spacing: -1px; }
        .hero-subtitle { opacity: 0.85; max-width: 700px; margin: 0 auto; font-family: 'Inter', sans-serif; }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; max-height: 75vh; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }
        .icon-circle-mini.active { background: #002b5a; color: #fff; border-color: #002b5a; }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }

        /* Premium Card Styling */
        .tour-premium-card { border-radius: 24px !important; transition: 0.4s cubic-bezier(0.165, 0.84, 0.44, 1); }
        .card-visual-header { height: 160px; background: #001d3d; position: relative; overflow: hidden; }
        .visual-overlay { position: absolute; inset: 0; background: linear-gradient(45deg, rgba(0,29,61,0.8), transparent); }
        .play-hint { 
            position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
            width: 50px; height: 50px; background: rgba(255,255,255,0.2); 
            backdrop-filter: blur(5px); border-radius: 50%; display: flex; 
            align-items: center; justify-content: center; color: #fff;
            transition: 0.3s;
        }
        .tour-premium-card:hover .play-hint { background: #ffc300; color: #000; scale: 1.1; }
        .card-footer-area { transition: 0.3s; }
        .tour-action-circle { 
            width: 40px; height: 40px; border-radius: 50%; background: #f1f5f9;
            display: flex; align-items: center; justify-content: center;
            color: #002b5a; transition: 0.3s;
        }
        .tour-premium-card:hover .tour-action-circle { background: #002b5a; color: #fff; }

        .live-dot-mini {
            width: 6px; height: 6px; background: #0055a4; border-radius: 50%;
            display: inline-block; animation: pulseMini 2s infinite;
        }
        @keyframes pulseMini { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }

        @media (max-width: 991px) {
          .section-standard-spacing { padding: 40px 0; }
          .main-portal-container { margin-top: 0px; padding-top: 0px; }
          .explorer-hero { padding: 40px 0 80px 0; }
          .hero-title { font-size: 2rem; }
          .hero-subtitle { font-size: 0.95rem; margin-bottom: 2rem !important; }
          .hero-icon-circle { width: 60px; height: 60px; font-size: 2rem; }
          .portal-heading { word-break: break-word; }
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 18px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        /* Navigation Hint Pulse */
        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0.4); }
          70% { box-shadow: 0 0 0 15px rgba(255, 195, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 195, 0, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          background: #002b5a !important;
          border-color: #ffc300 !important;
        }
      `}</style>
    </div>
  );
};

export default CampusTour;
